import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { api } from "../../api";
import { StatCard } from "../../components/common/StatCard";
import { ErrorState } from "../../components/common/ErrorState";
import { AtRiskGymsPanel } from "../../components/control/AtRiskGymsPanel";
import { PlatformHealthStrip } from "../../components/control/PlatformHealthStrip";
import { RevenueBreakdownPanel } from "../../components/control/RevenueBreakdownPanel";
import { GymsByStatusChart } from "../../components/control/GymsByStatusChart";
import { NewGymsTrendChart } from "../../components/control/NewGymsTrendChart";
import { TrialsPanel } from "../../components/control/TrialsPanel";
import { ChurnedGymsPanel } from "../../components/control/ChurnedGymsPanel";
import { CONTROL } from "../../constants/testIds";
import { useKairooData } from "../../contexts/KairooDataContext";
import { formatCurrency } from "../../lib/format";
import { fadeUp } from "../../lib/motion";

// UX-CP-01 §1 SCR-P08-01 "Platform Dashboard" — every figure sourced from the real
// `/control/platform-summary` read-model; nothing computed client-side.
// DEFECT-D-19 to D-23: rebuilt as a real chart-based dashboard (donut + trend line, not just
// number tiles), every KPI tile/chart segment drills into the exact filtered screen it
// describes (D-22), and the additional KPIs Kairoo asked for (D-21) are included with their
// own rationale shown inline.
export default function ControlDashboard() {
  const { me } = useKairooData();
  const reducedMotion = useReducedMotion();
  const navigate = useNavigate();

  const summaryQ = useQuery({
    queryKey: ["platform-summary"],
    queryFn: async () => (await api.get("/control/platform-summary")).data,
  });

  if (summaryQ.isError) {
    return <ErrorState error={summaryQ.error} onRetry={() => summaryQ.refetch()} title="Couldn't load the Platform Dashboard" />;
  }

  const s = summaryQ.data;
  const d = s?.dashboard;
  const loading = summaryQ.isLoading;

  return (
    <div data-testid={CONTROL.dashboardPage} className="space-y-6 max-w-7xl pb-8">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-3xl font-bold" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}>
          Platform Dashboard
        </h1>
        <p className="text-base mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>
          Welcome back, {me?.name?.split(" ")[0] || "operator"}. Here's how FitFrikArena is doing across every gym.
        </p>
      </motion.div>

      <PlatformHealthStrip health={s?.health_summary} loading={loading} />

      <motion.div {...fadeUp(reducedMotion, 0.03)} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId={CONTROL.kpiCard("gyms-total")} label="Gyms on platform" value={s?.gyms_total ?? 0} loading={loading} index={0} onClick={() => navigate("/kairoo/gyms")} />
        <StatCard testId={CONTROL.kpiCard("live-users")} label="Live users right now" value={d?.live_users_right_now ?? 0} loading={loading} index={1} />
        <StatCard testId={CONTROL.kpiCard("checkins-today")} label="Check-ins today" value={d?.checkins_today ?? 0} loading={loading} index={2} />
        <StatCard testId={CONTROL.kpiCard("members-total")} label="Members across platform" value={d?.members_total ?? 0} loading={loading} index={3} />
        <StatCard testId={CONTROL.kpiCard("staff-total")} label="Staff accounts" value={d?.staff_total ?? 0} loading={loading} index={4} />
        <StatCard testId={CONTROL.kpiCard("branches-total")} label="Branches" value={d?.branches_total ?? 0} loading={loading} index={5} />
        <StatCard testId={CONTROL.kpiCard("gmv-total")} label="Total processed (GMV)" formatValue={(v) => formatCurrency(v ?? 0)} value={d?.gmv_total ?? 0} loading={loading} index={6} />
        <StatCard
          testId={CONTROL.additionalKpiCard("arpg")} label="Avg revenue / paying gym" formatValue={(v) => formatCurrency(v ?? 0)}
          value={d?.additional_kpis?.arpg ?? 0} subtitle={d?.additional_kpis?.arpg_rationale} loading={loading} index={7}
        />
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <GymsByStatusChart byStatus={s?.gyms_by_status} loading={loading} />
        <NewGymsTrendChart trend={d?.new_gyms_trend} thisWeek={d?.new_gyms_this_week} lastWeek={d?.new_gyms_last_week} loading={loading} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <RevenueBreakdownPanel revenue={s?.platform_revenue} dashboardRevenue={d?.revenue} loading={loading} />
        <TrialsPanel activeTrials={d?.active_trials} conversionRate={d?.trial_conversion_rate} conversionNote={d?.trial_conversion_note} loading={loading} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <AtRiskGymsPanel gyms={s?.at_risk_gyms} loading={loading} />
        <ChurnedGymsPanel churned={d?.churned_gyms} loading={loading} />
      </div>

      {s?.gyms_by_status_note && (
        <p className="text-xs max-w-2xl" style={{ color: "var(--scarlet-text-secondary)" }}>{s.gyms_by_status_note}</p>
      )}
    </div>
  );
}
